import { useResyncBuilding } from '@/api/building'
import { useGetAccentraStatus } from '@/api/property-keyless/accentra/get-accentra-status'
import { useUpdateAccentraConnection } from '@/api/property-keyless/accentra/update-accentra-connection'
import { useGetBrivoConnection } from '@/api/property-keyless/brivo/get-brivo-connection'
import { useUpdateBrivoConnection } from '@/api/property-keyless/brivo/update-brivo-connection'
import { useGetDormakabaConnection } from '@/api/property-keyless/dormakaba/get-dormakaba-connection'
import { useUpdateDormakabaConnection } from '@/api/property-keyless/dormakaba/update-dormakaba-connection'
import { useGetIglooWorksStatus } from '@/api/property-keyless/igloo-works/get-igloo-works-status'
import { useUpdateIglooWorksConnection } from '@/api/property-keyless/igloo-works/update-igloo-works-connection'
import { useGetPointCentralStatus } from '@/api/property-keyless/point-central/get-point-central-status'
import { useUpdatePointCentralConnection } from '@/api/property-keyless/point-central/update-point-central-connection'
import {
  SaltoSpaceLevelRef,
  SaltoSpaceZoneRef,
  useGetSaltoSpaceConnection,
} from '@/api/property-keyless/salto-space/get-salto-space-connection'
import { useUpdateSaltoSpaceConnection } from '@/api/property-keyless/salto-space/update-salto-space-connection'
import { useUpdateSwiftlaneConnection } from '@/api/property-keyless/swiftlane/update-swiftlane-connection'

// Note: these hooks wrap the shared property-keyless ones (also used outside Places) and only add
// what the credentials cards need on top: a `configured` flag and the building re-read after saves
// that turn an availability flag on. A 400/404 from the status endpoints is the "nothing stored
// yet" state, which leaves `data` empty, so `configured` is simply false then.

export const useBrivoCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetBrivoConnection({ buildingId })

  return {
    clientId: data?.clientId ?? '',
    clientSecret: data?.clientSecret ?? '',
    configured: Boolean(data?.clientSecret),
    isLoading,
  }
}

export const useSaveBrivoCredentials = useUpdateBrivoConnection

export const useIglooWorkCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetIglooWorksStatus({ buildingId })

  return {
    clientId: data?.clientId ?? '',
    configured: Boolean(data),
    config: data?.config,
    isLoading,
  }
}

const EMPTY_IGLOO_CONFIG = {
  permissionDoors: {},
  shouldAutoAssignApartmentDoor: false,
  shouldAutoAssignApartmentDoorVisitor: false,
  visitorPermissionDoors: {},
}

type SaveIglooWorkCredentialsRequest = {
  buildingId: number
  clientId: string
  clientSecret: string
  config?: typeof EMPTY_IGLOO_CONFIG
}

// Note: connecting replaces the stored config on the backend, so the stored door mappings are sent back
export const useSaveIglooWorkCredentials = () => {
  const { mutateAsync, ...rest } = useUpdateIglooWorksConnection()

  return {
    ...rest,
    mutateAsync: ({ config, ...data }: SaveIglooWorkCredentialsRequest) =>
      mutateAsync({ ...data, config: config ?? EMPTY_IGLOO_CONFIG }),
  }
}

export const useDormakabaCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetDormakabaConnection({ buildingId })
  const stored = data as { host?: string | null; scope?: string | null } | undefined

  return {
    configured: Boolean(data),
    host: stored?.host ?? '',
    isLoading,
    scope: stored?.scope ?? '',
  }
}

type SaveDormakabaCredentialsRequest = {
  buildingId: number
  clientId: string
  clientSecret: string
  host: string
  password: string
  scope: string
  username: string
}

// Note: the backend fills the omitted connection fields with the same defaults, so they are sent as such
export const useSaveDormakabaCredentials = () => {
  const { mutateAsync, ...rest } = useUpdateDormakabaConnection()
  const resyncBuilding = useResyncBuilding()

  return {
    ...rest,
    mutateAsync: async (data: SaveDormakabaCredentialsRequest) => {
      await mutateAsync({
        ...data,
        protocol: 'https',
        requesterId: 'WS1',
        tokenHost: data.host,
        tokenProtocol: 'https',
      })
      await resyncBuilding(data.buildingId)
    },
  }
}

export const usePointCentralCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetPointCentralStatus({ buildingId })

  return { configured: Boolean(data?.isConnected), isLoading }
}

export const useSavePointCentralCredentials = useUpdatePointCentralConnection

export const useAccentraCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetAccentraStatus({ buildingId })

  return { configured: Boolean(data?.isConnected), isLoading }
}

export const useSaveAccentraCredentials = useUpdateAccentraConnection

export const useSaveSwiftlaneCredentials = useUpdateSwiftlaneConnection

export interface SaltoSpaceAccessItem {
  name: string
  saltoId: string
}

export interface SaltoSpaceIntegrationConfig {
  accessLevels: SaltoSpaceAccessItem[]
  permissionGroups: SaltoSpaceAccessItem[]
  shouldAutoAssignApartmentDoor: boolean
  shouldAutoAssignUserAccessLevel: boolean
  visitorAccessLevels: SaltoSpaceAccessItem[]
  visitorPermissionGroups: SaltoSpaceAccessItem[]
}

export interface SaltoSpaceIntegration {
  apiKey: string
  config: SaltoSpaceIntegrationConfig
  configured: boolean
  url: string
}

const fromZones = (items: SaltoSpaceZoneRef[] = []): SaltoSpaceAccessItem[] =>
  items.map(({ ExtZoneID, name }) => ({ name, saltoId: String(ExtZoneID) }))

const fromLevels = (items: SaltoSpaceLevelRef[] = []): SaltoSpaceAccessItem[] =>
  items.map(({ ExtGroupID, name }) => ({ name, saltoId: String(ExtGroupID) }))

const toZones = (items: SaltoSpaceAccessItem[]) =>
  items.map(({ name, saltoId }) => ({ ExtZoneID: saltoId, name }))

const toLevels = (items: SaltoSpaceAccessItem[]) =>
  items.map(({ name, saltoId }) => ({ ExtGroupID: saltoId, name }))

export const useSaltoSpaceCredentials = (buildingId: number) => {
  const { data, isLoading } = useGetSaltoSpaceConnection({ buildingId })

  const integration: SaltoSpaceIntegration | undefined = data && {
    apiKey: data.apiKey ?? '',
    config: {
      accessLevels: fromLevels(data.config?.accessLevels),
      permissionGroups: fromZones(data.config?.permissionGroups),
      shouldAutoAssignApartmentDoor: !!data.config?.shouldAutoAssignApartmentDoor,
      shouldAutoAssignUserAccessLevel: !!data.config?.shouldAutoAssignUserAccessLevel,
      visitorAccessLevels: fromLevels(data.config?.visitorAccessLevels),
      visitorPermissionGroups: fromZones(data.config?.visitorPermissionGroups),
    },
    configured: Boolean(data.apiKey),
    url: data.url ?? '',
  }

  return { integration, isLoading }
}

type SaveSaltoSpaceCredentialsRequest = {
  apiKey: string
  buildingId: number
  config: SaltoSpaceIntegrationConfig
  url: string
}

// Note: saving the integration also turns saltoSpaceAvailable on, so the stored building is re-read
export const useSaveSaltoSpaceCredentials = () => {
  const { mutateAsync, ...rest } = useUpdateSaltoSpaceConnection()
  const resyncBuilding = useResyncBuilding()

  return {
    ...rest,
    mutateAsync: async ({ config, ...data }: SaveSaltoSpaceCredentialsRequest) => {
      await mutateAsync({
        ...data,
        config: {
          accessLevels: toLevels(config.accessLevels),
          permissionGroups: toZones(config.permissionGroups),
          shouldAutoAssignApartmentDoor: config.shouldAutoAssignApartmentDoor,
          shouldAutoAssignUserAccessLevel: config.shouldAutoAssignUserAccessLevel,
          visitorAccessLevels: toLevels(config.visitorAccessLevels),
          visitorPermissionGroups: toZones(config.visitorPermissionGroups),
        },
      })
      await resyncBuilding(data.buildingId)
    },
  }
}
